import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight, Loader2, Sparkles, Target } from "lucide-react";
import { submitLead } from "@/lib/api";
import { BUSINESS_TYPES } from "@/lib/constants";

const TRADE_FRIENDLY_LABEL = {
  "Mechanic / Garage": "Mechanics",
  "Plumber / Tradesman": "Plumbers",
  "Electrician": "Electricians",
  "Salon / Beauty": "Salons",
  "Coaching": "Coaches",
  "Other Service Business": "your business",
};

const LeadDialogContext = createContext(null);

export function useLeadDialog() {
  const ctx = useContext(LeadDialogContext);
  if (!ctx) throw new Error("useLeadDialog must be used inside LeadDialogProvider");
  return ctx;
}

const COPY = {
  free_preview: {
    overline: "",
    title: "Get your free website preview",
    description:
      "Tell us about your business and we'll send back a personalised website preview. No payment, no obligation.",
    cta: "Send my free preview",
  },
  quote: {
    overline: "Get a Quote",
    title: "Tell us what you need",
    description:
      "Share a few details about your business and goals. We'll come back with a tailored quote within 24 hours.",
    cta: "Request quote",
  },
  contact: {
    overline: "Contact",
    title: "Get in touch",
    description: "Drop us a line and we'll respond shortly.",
    cta: "Send message",
  },
};

const initial = {
  business_name: "",
  contact_name: "",
  email: "",
  phone: "",
  business_type: "",
  location: "",
  current_website: "",
  message: "",
};

export function LeadDialogProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("free_preview");
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const openDialog = useCallback((nextType, preset) => {
    setType(nextType);
    // Only accept plain config objects; ignore React SyntheticEvents passed by onClick
    const safePreset =
      preset && typeof preset === "object" && !preset.nativeEvent
        ? preset
        : {};
    setForm({ ...initial, ...safePreset });
    setOpen(true);
  }, []);

  const value = useMemo(
    () => ({
      openPreview: (preset) => openDialog("free_preview", preset),
      openQuote: (preset) => openDialog("quote", preset),
      openContact: (preset) => openDialog("contact", preset),
    }),
    [openDialog],
  );

  const copy = COPY[type];

  const onChange = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target?.value ?? e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email) {
      toast.error("Please fill in your business name, name, and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({ type, ...form });
      toast.success(
        type === "free_preview"
          ? "Preview request received. We'll be in touch shortly."
          : "Got it. We'll respond within 24 hours.",
      );
      setOpen(false);
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LeadDialogContext.Provider value={value}>
      {children}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="lead-form-dialog"
          className="bg-[#0a0c10] border border-white/10 text-white max-w-lg sm:max-w-xl p-0 max-h-[90vh] sm:max-h-[88vh] gap-0 flex flex-col overflow-hidden"
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent pointer-events-none z-10" />
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="lead-form-scroll-area"
            className="p-6 sm:p-8 overflow-y-auto overscroll-contain flex-1 min-h-0"
          >
            <DialogHeader className="space-y-3 text-left">
              {copy.overline && (
                <span className="inline-flex w-fit items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-semibold">
                  <Sparkles className="w-3.5 h-3.5" />
                  {copy.overline}
                </span>
              )}
              {form.business_type && (
                <span
                  data-testid="lead-form-tailored-pill"
                  className="inline-flex w-fit items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/[0.06] text-xs text-emerald-200"
                >
                  <Target className="w-3.5 h-3.5 text-emerald-300" />
                  Tailored for{" "}
                  <span className="font-semibold text-white">
                    {TRADE_FRIENDLY_LABEL[form.business_type] || form.business_type}
                  </span>
                </span>
              )}
              <DialogTitle className="font-display text-2xl sm:text-3xl tracking-tight font-semibold">
                {copy.title}
              </DialogTitle>
              <DialogDescription className="text-white/60 leading-relaxed">
                {copy.description}
              </DialogDescription>
            </DialogHeader>

            <form
              onSubmit={onSubmit}
              className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <FieldWrapper label="Business name *">
                <Input
                  data-testid="lead-form-business-name"
                  value={form.business_name}
                  onChange={onChange("business_name")}
                  placeholder="e.g. McCarthy Motors"
                  required
                  className="bg-black/40 border-white/10 focus-visible:ring-emerald-400/40 focus-visible:border-emerald-400/50"
                />
              </FieldWrapper>
              <FieldWrapper label="Your name *">
                <Input
                  data-testid="lead-form-contact-name"
                  value={form.contact_name}
                  onChange={onChange("contact_name")}
                  placeholder="John Murphy"
                  required
                  className="bg-black/40 border-white/10 focus-visible:ring-emerald-400/40 focus-visible:border-emerald-400/50"
                />
              </FieldWrapper>
              <FieldWrapper label="Email *">
                <Input
                  type="email"
                  data-testid="lead-form-email"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="you@business.ie"
                  required
                  className="bg-black/40 border-white/10 focus-visible:ring-emerald-400/40 focus-visible:border-emerald-400/50"
                />
              </FieldWrapper>
              <FieldWrapper label="Phone">
                <Input
                  data-testid="lead-form-phone"
                  value={form.phone}
                  onChange={onChange("phone")}
                  placeholder="085 123 4567"
                  className="bg-black/40 border-white/10 focus-visible:ring-emerald-400/40 focus-visible:border-emerald-400/50"
                />
              </FieldWrapper>
              <FieldWrapper label="Business type">
                <Select
                  value={form.business_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}
                >
                  <SelectTrigger
                    data-testid="lead-form-business-type"
                    className="bg-black/40 border-white/10 focus:ring-emerald-400/40"
                  >
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0c10] border-white/10 text-white">
                    {BUSINESS_TYPES.map((b) => (
                      <SelectItem
                        key={b}
                        value={b}
                        className="focus:bg-emerald-500/10 focus:text-white"
                      >
                        {b}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldWrapper>
              <FieldWrapper label="Location (Ireland)">
                <Input
                  data-testid="lead-form-location"
                  value={form.location}
                  onChange={onChange("location")}
                  placeholder="Cork, Dublin, Galway…"
                  className="bg-black/40 border-white/10 focus-visible:ring-emerald-400/40 focus-visible:border-emerald-400/50"
                />
              </FieldWrapper>
              <FieldWrapper label="Current website (optional)" full>
                <Input
                  data-testid="lead-form-current-website"
                  value={form.current_website}
                  onChange={onChange("current_website")}
                  placeholder="https://…"
                  className="bg-black/40 border-white/10 focus-visible:ring-emerald-400/40 focus-visible:border-emerald-400/50"
                />
              </FieldWrapper>
              <FieldWrapper label="What do you need?" full>
                <Textarea
                  data-testid="lead-form-message"
                  value={form.message}
                  onChange={onChange("message")}
                  rows={4}
                  placeholder="A few words about your business and goals…"
                  className="bg-black/40 border-white/10 resize-none focus-visible:ring-emerald-400/40 focus-visible:border-emerald-400/50"
                />
              </FieldWrapper>

              <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row sm:items-center gap-3 pt-2">
                <p className="text-xs text-white/40">
                  No spam. Your info stays with Website Walsh only.
                </p>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="lead-form-submit"
                  className="sm:ml-auto bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-black font-semibold rounded-full px-6 py-5 hover:shadow-[0_0_24px_rgba(16,185,129,0.4)] transition-all"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      {copy.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </DialogContent>
      </Dialog>
    </LeadDialogContext.Provider>
  );
}

function FieldWrapper({ label, children, full = false }) {
  return (
    <div className={full ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
      <Label className="text-xs uppercase tracking-[0.15em] text-white/50 font-medium">
        {label}
      </Label>
      {children}
    </div>
  );
}
